import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Modal } from 'react-bootstrap';
import { IKPI } from '../../../../../../../model/measure/IMeasure';
import { WarningToast } from '../../../../../../../utils/toasters';
import { useTranslation } from 'react-i18next';

interface IModalKPI {
    show: boolean;
    setModalData: (...args: any) => void;
    saveFunc: (...args: any) => void;
    setKpis: (...args: any) => void;
    kpis?: IKPI[];
    additionalKpi?: IKPI[];
}

export default function ModalKPI({ show, setModalData, saveFunc, setKpis, kpis, additionalKpi }: IModalKPI) {
    const { t } = useTranslation();
    const lastKpiRef = useRef<HTMLElement>(null);
    const [canScroll, setCanScroll] = useState(false);

    const handleKpis = (e: React.ChangeEvent<any>, index: number) => {
        const { name, value } = e.target as HTMLInputElement;
        setKpis((prevKpis: IKPI[]) => {
            const updatedKpis = [...prevKpis];
            updatedKpis[index] = {
                ...updatedKpis[index],
                [name]: value,
                [`valid${name}`]: {
                    isValid: !!value,
                    startValid: true,
                },
            };

            return updatedKpis;
        });
    };

    const addKpi = () => {
        setCanScroll(true);
        if (kpis?.length === 10) {
            return WarningToast(t('brand.campaign.measure.maximumKpi'));
        }
        setKpis((prevKpis: IKPI[]) => {
            return [
                ...prevKpis,
                {
                    title: '',
                    value: '',
                    validtitle: { isValid: true, startValid: false },
                    validvalue: { isValid: true, startValid: false },
                },
            ];
        });
    };

    useEffect(() => {
        if (canScroll) {
            lastKpiRef?.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    }, [kpis?.length]);

    const removeKpi = (index: number) => {
        setCanScroll(false);
        const filteredKpis = kpis?.filter((kpi, i) => i !== index);
        setKpis(filteredKpis);
    };

    const closeModal = () => {
        setModalData((prev: any) => ({ ...prev, show: false }));
        const updatedKpi = additionalKpi?.map(item => ({
            ...item,
            validtitle: { isValid: true, startValid: false },
            validvalue: { isValid: true, startValid: false },
        }));
        setKpis(updatedKpi);
    };

    return (
        <Modal show={show} onHide={() => closeModal()}>
            <Modal.Header>
                <Modal.Title>{t('brand.campaign.measure.customKPI')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div style={{ maxHeight: '320px' }} className="overflow-auto px-1">
                    {kpis?.map((kpi, index) => {
                        return (
                            <div key={index} className="d-flex gap-3 mb-3 align-items-end">
                                <Col>
                                    <h6>
                                        {t('brand.campaign.measure.title')} ({index + 1})
                                    </h6>
                                    <Form.Control
                                        className={`${
                                            kpi.validtitle?.startValid
                                                ? kpi.validtitle?.isValid
                                                    ? ''
                                                    : 'border-danger'
                                                : ''
                                        }`}
                                        value={kpi.title}
                                        name="title"
                                        maxLength={40}
                                        onChange={e => handleKpis(e, index)}
                                    />
                                </Col>
                                <Col>
                                    <h6>
                                        {t('brand.campaign.measure.value')} ({index + 1})
                                    </h6>
                                    <Form.Control
                                        className={`${
                                            kpi.validvalue?.startValid
                                                ? kpi.validvalue?.isValid
                                                    ? ''
                                                    : 'border-danger'
                                                : ''
                                        }`}
                                        name="value"
                                        value={kpi.value}
                                        maxLength={20}
                                        onChange={e => handleKpis(e, index)}
                                    />
                                </Col>
                                <Col className="col-auto">
                                    <div className="btn btn-outline-danger pb-0 pt-1" onClick={() => removeKpi(index)}>
                                        <i className="fi fi-rr-trash font-16"></i>
                                    </div>
                                </Col>
                            </div>
                        );
                    })}
                    <span ref={lastKpiRef} />
                </div>
                {(kpis?.length ?? 0) < 10 && (
                    <Button className="mt-3" onClick={() => addKpi()}>
                        {t('brand.campaign.measure.add_more')}
                    </Button>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={() => closeModal()} className="btn-outline-primary">
                    {t('general.button.cancel')}
                </Button>
                <Button onClick={() => saveFunc()}>{t('general.button.save')}</Button>
            </Modal.Footer>
        </Modal>
    );
}
